import { Injectable, inject } from '@angular/core';
import { generateClient } from 'aws-amplify/api';
import * as queries from '@graphql/queries';
import {
    UpdateProductoInput,
    Producto,
    CreateProductoInput
} from '@app/core/services/api/API';
import { AmplifyApiService } from './amplify-api.service';

const GET_PRODUCTO_FLAGS = `
query GetProductoFlags($id: ID!) {
  getProducto(id: $id) {
    id
    providerId
    categoryId
    name
    status
    bestSeller
    price
    isAutoSkipped
    isAllowable
    isRestricted
    pgClasitication
    visibleFor
  }
}
`;

const UPDATE_PRODUCTO_FLAGS = `
mutation UpdateProductoFlags($input: UpdateProductoInput!) {
  updateProducto(input: $input) {
    id
    providerId
    categoryId
    name
    status
    bestSeller
    price
    isAutoSkipped
    isAllowable
    isRestricted
    pgClasitication
    visibleFor
  }
}
`;

const PRODUCTO_SAVE_SELECTION = `
    id
    providerId
    categoryId
    name
    status
    bestSeller
    images {
      key
    }
    order
    sku
    brand
    delivery_time
    delivery_time_units
    stock
    minRequest
    maxRequest
    article_of_clothing
    size
    price
    long_description
    short_description
    isPurse
    hasBaseAmmount
    isFreeAmmount
    isByRequest
    isMultiPrice
    cardBaseAmmount
    minCardBaseAmmount
    maxCardBaseAmmount
    unspsc
    variants {
      sku
      name
      description
      filterColors
      image {
        key
      }
      stock_available
      price
      size
      logoType
    }
    visibleFor
    filterColors
    createdAt
    updatedAt
`;

const CREATE_PRODUCTO = `
mutation CreateProducto($input: CreateProductoInput!) {
  createProducto(input: $input) {
    ${PRODUCTO_SAVE_SELECTION}
  }
}
`;

const UPDATE_PRODUCTO = `
mutation UpdateProducto($input: UpdateProductoInput!) {
  updateProducto(input: $input) {
    ${PRODUCTO_SAVE_SELECTION}
  }
}
`;

@Injectable({
    providedIn: 'root'
})
export class ProductService {
    private amplifyClient = generateClient();
    private _amplifyApiService = inject(AmplifyApiService);

    constructor() {}

    async listProductos(): Promise<Producto[]> {
        try {
            const response = await this.amplifyClient.graphql({
                query: queries.listProductos
            });

            if (response.errors) {
                console.error('GraphQL errors:', response.errors);
                throw new Error('Error al consultar productos');
            }

            const data = response.data.listProductos.items;
            if (data) return data as Producto[];

            console.warn('No se encontraron productos');
            return [];
        } catch (error) {
            // console.error('Error finding ExternalApis:', error);
            return [];
        }
    }

    async getProducto(id: string): Promise<Producto> {
        try {
            const response = await this.amplifyClient.graphql({
                query: queries.getProducto,
                variables: {
                    id
                }
            });
            if ('data' in response && response.data?.getProducto) {
                return response.data.getProducto as Producto;
            }
        } catch (error) {
            console.error('Error finding ExternalApis:', error);
        }
        return null;
    }

    async productsByAutoAllowed(isAutoAllowed: string): Promise<Producto[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.productsByAutoAllowed,
                    variables: {
                        isAutoAllowed
                    }
                })
            ).data.productsByAutoAllowed.items;
            if (data) return data as Producto[];
        } catch (error) {
            console.error('Error product:', error);
            return null;
        }
    }

    async productsByProviderStatus(providerId: string): Promise<Producto[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.productsByProviderStatus,
                    variables: {
                        providerId
                    }
                })
            ).data.productsByProviderStatus.items;
            if (data) return data as Producto[];
        } catch (error) {
            console.error('Error product:', error);
            return null;
        }
    }

    async productsByProviderCategory(providerId: string): Promise<Producto[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.productsByProviderCategory,
                    variables: {
                        providerId
                    }
                })
            ).data.productsByProviderCategory.items;
            if (data) return data as Producto[];
        } catch (error) {
            console.error('Error product:', error);
            return null;
        }
    }

    async productsByCategory(categoryId: string): Promise<Producto[]> {
        try {
            const response = await this.amplifyClient.graphql({
                query: queries.productsByCategory,
                variables: {
                    categoryId
                }
            });
            const data = response.data.productsByCategory.items;
            if (data) {
                return data as Producto[];
            }
            return [];
        } catch (error) {
            console.error('Error al obtener productos por categoría:', error);
            return [];
        }
    }

    async productsByCategoryPrice(categoryId: string): Promise<Producto[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.productsByCategoryPrice,
                    variables: {
                        categoryId
                    }
                })
            ).data.productsByCategoryPrice.items;
            if (data) return data as Producto[];
        } catch (error) {
            console.error('Error product:', error);
            return null;
        }
    }

    async productsByCategoryProviderPrice(
        categoryId: string
    ): Promise<Producto[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.ProductsByCategoryProviderPrice,
                    variables: {
                        categoryId
                    }
                })
            ).data.ProductsByCategoryProviderPrice.items;
            if (data) return data as Producto[];
        } catch (error) {
            console.error('Error product:', error);
            return null;
        }
    }

    async productsByBestSellers(bestSeller: string): Promise<Producto[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.productsByBestSellers,
                    variables: {
                        bestSeller
                    }
                })
            ).data.productsByBestSellers.items;
            if (data) return data as Producto[];
        } catch (error) {
            console.error('Error product:', error);
            return null;
        }
    }
    async productBySku(sku: string): Promise<Producto[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.productBySku,
                    variables: {
                        sku
                    }
                })
            ).data.productBySku.items;
            if (data) return data as Producto[];
        } catch (error) {
            console.error('Error product:', error);
            return null;
        }
    }

    async ProductsByName(name: string): Promise<Producto[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.ProductsByName,
                    variables: {
                        name
                    }
                })
            ).data.ProductsByName.items;
            if (data) return data as Producto[];
        } catch (error) {
            console.error('Error product:', error);
            return null;
        }
    }

    async createProducto(input: CreateProductoInput): Promise<Producto> {
        const sanitized = this.sanitizeProductoSaveInput(input);
        try {
            const response = await this._amplifyApiService.graphql<{
                data?: { createProducto?: Producto | null };
            }>({
                query: CREATE_PRODUCTO,
                variables: {
                    input: sanitized
                }
            });
            if (response.data?.createProducto) {
                return response.data.createProducto as Producto;
            }
        } catch (error) {
            const product = this.graphqlErrorData(error)?.createProducto;
            if (product?.id) {
                return product as Producto;
            }
            console.error(
                'Error creating product:',
                this.graphqlErrorMessages(error)
            );
        }
        return null;
    }

    /**
     * Solo campos escalares. getProducto generado pide provider/category/images
     * y AppSync responde 4 errores (hasOne + File.palette).
     */
    async getProductoStatusFlags(id: string): Promise<Producto | null> {
        try {
            const response = await this._amplifyApiService.graphql<{
                data?: { getProducto?: Producto | null };
            }>({
                query: GET_PRODUCTO_FLAGS,
                variables: { id }
            });
            return this.pickProductoFlags(response?.data?.getProducto);
        } catch (error) {
            const product = this.graphqlErrorData(error)?.getProducto;
            if (product) {
                return this.pickProductoFlags(product);
            }
            console.error(
                'Error finding product status flags:',
                this.graphqlErrorMessages(error)
            );
            return null;
        }
    }

    /**
     * Mutation liviana. Si cambia `bestSeller` o `price`, AppSync exige las
     * demás claves de los GSI que usan price (providerId, categoryId, name).
     */
    async updateProductoFlags(input: UpdateProductoInput): Promise<Producto> {
        const sanitized = await this.completeProductoIndexKeys(
            this.sanitizeProductoFlagsInput(input)
        );
        try {
            const response = await this._amplifyApiService.graphql<{
                data?: { updateProducto?: Producto | null };
            }>({
                query: UPDATE_PRODUCTO_FLAGS,
                variables: { input: sanitized }
            });
            return this.pickProductoFlags(response?.data?.updateProducto);
        } catch (error) {
            const product = this.graphqlErrorData(error)?.updateProducto;
            if (product) {
                return this.pickProductoFlags(product);
            }
            console.error(
                'Error updating product flags:',
                this.graphqlErrorMessages(error)
            );
            return null;
        }
    }

    private pickProductoFlags(
        product: Producto | null | undefined
    ): Producto | null {
        if (!product?.id) {
            return null;
        }
        return {
            id: product.id,
            providerId: product.providerId,
            categoryId: product.categoryId,
            name: product.name,
            status: product.status,
            bestSeller: product.bestSeller,
            price: product.price,
            isAutoSkipped: product.isAutoSkipped,
            isAllowable: product.isAllowable,
            isRestricted: product.isRestricted,
            pgClasitication: product.pgClasitication,
            visibleFor: product.visibleFor
        } as Producto;
    }

    /**
     * Completa claves de índices Amplify al tocar bestSeller/price/status.
     * byBestSellers: bestSeller + price
     * byCategoryProviderPrice: categoryId + providerId + price
     * byCategoryPrice: categoryId + price
     * byProductName: name + price
     * byProviderStatus: providerId + status
     */
    private async completeProductoIndexKeys(
        input: UpdateProductoInput
    ): Promise<UpdateProductoInput> {
        const touchesPriceIndex =
            input.bestSeller !== undefined || input.price !== undefined;
        const touchesStatusIndex = input.status !== undefined;
        if (!touchesPriceIndex && !touchesStatusIndex) {
            return input;
        }

        const current = await this.getProductoStatusFlags(input.id);
        const completed = { ...input };

        if (touchesPriceIndex) {
            const price = Number(completed.price ?? current?.price);
            if (Number.isFinite(price)) {
                completed.price = price;
            }
            completed.providerId =
                completed.providerId || current?.providerId || undefined;
            completed.categoryId =
                completed.categoryId || current?.categoryId || undefined;
            completed.name = completed.name || current?.name || undefined;
        }

        if (touchesStatusIndex) {
            completed.providerId =
                completed.providerId || current?.providerId || undefined;
        }

        return this.sanitizeProductoFlagsInput(completed);
    }

    private sanitizeProductoFlagsInput(
        input: UpdateProductoInput
    ): UpdateProductoInput {
        const sanitized: UpdateProductoInput = { id: input.id };
        const keys: Array<keyof UpdateProductoInput> = [
            'providerId',
            'categoryId',
            'name',
            'status',
            'bestSeller',
            'price',
            'isAutoSkipped',
            'isAllowable',
            'isRestricted',
            'pgClasitication',
            'visibleFor'
        ];
        for (const key of keys) {
            const value = input[key];
            if (value !== undefined) {
                (sanitized as Record<string, unknown>)[key as string] = value;
            }
        }
        if (sanitized.price != null) {
            const price = Number(sanitized.price);
            if (Number.isFinite(price)) {
                sanitized.price = price;
            } else {
                delete sanitized.price;
            }
        }
        return sanitized;
    }

    private graphqlErrorData(error: unknown): Record<string, any> | null {
        if (!error || typeof error !== 'object') {
            return null;
        }
        const withData = error as { data?: Record<string, any> };
        return withData.data ?? null;
    }

    private graphqlErrorMessages(error: unknown): string[] {
        if (!error || typeof error !== 'object') {
            return [String(error)];
        }
        const withErrors = error as {
            errors?: Array<{ message?: string; path?: unknown }>;
            message?: string;
        };
        if (Array.isArray(withErrors.errors) && withErrors.errors.length) {
            return withErrors.errors.map(item => {
                const path = Array.isArray(item.path)
                    ? item.path.join('.')
                    : '';
                return path
                    ? `${path}: ${item.message ?? 'GraphQL error'}`
                    : item.message ?? 'GraphQL error';
            });
        }
        if (withErrors.message) {
            return [withErrors.message];
        }
        return [JSON.stringify(error)];
    }

    async updateProducto(input: UpdateProductoInput): Promise<Producto> {
        const sanitized = this.sanitizeProductoSaveInput(input);
        try {
            const response = await this._amplifyApiService.graphql<{
                data?: { updateProducto?: Producto | null };
            }>({
                query: UPDATE_PRODUCTO,
                variables: {
                    input: sanitized
                }
            });
            if (response.data?.updateProducto) {
                return response.data.updateProducto as Producto;
            }
        } catch (error) {
            const product = this.graphqlErrorData(error)?.updateProducto;
            if (product?.id) {
                return product as Producto;
            }
            console.error(
                'Error updating product:',
                this.graphqlErrorMessages(error)
            );
        }
        return null;
    }

    /**
     * Igual que categoría: File solo con `key` (lambda S3). Evita palette/url
     * y convierte min/max a Int. unspsc vacío se manda null.
     */
    private sanitizeProductoSaveInput<
        T extends CreateProductoInput | UpdateProductoInput
    >(input: T): T {
        const sanitized = { ...input };
        if (Array.isArray(sanitized.images)) {
            sanitized.images = this.sanitizeFileInputs(sanitized.images);
        }
        if (Array.isArray(sanitized.variants)) {
            sanitized.variants = sanitized.variants.map(variant => {
                if (!variant) {
                    return variant;
                }
                return {
                    ...variant,
                    image: Array.isArray(variant.image)
                        ? this.sanitizeFileInputs(variant.image)
                        : variant.image
                };
            });
        }
        if ('minRequest' in sanitized) {
            sanitized.minRequest = this.toOptionalInt(sanitized.minRequest);
        }
        if ('maxRequest' in sanitized) {
            sanitized.maxRequest = this.toOptionalInt(sanitized.maxRequest);
        }
        if ('unspsc' in sanitized) {
            sanitized.unspsc = sanitized.unspsc
                ? String(sanitized.unspsc)
                : null;
        }
        return sanitized;
    }

    private sanitizeFileInputs(
        files: Array<{ key?: string | null } | null> | null | undefined
    ): Array<{ key: string }> {
        if (!Array.isArray(files)) {
            return [];
        }
        return files
            .map(file => (file?.key ? { key: file.key } : null))
            .filter((file): file is { key: string } => !!file);
    }

    private toOptionalInt(value: unknown): number | null {
        if (value === undefined || value === null || value === '') {
            return null;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
}
